import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Button, 
    Container,
    Paper,
    CircularProgress,
    Typography,
    useTheme,
    Stack,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Autocomplete,
    TextField,
    Popover,
    Fade,
    Zoom,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    RadioGroup,
    List,
    ListItem,
    FormControl,
    Radio,
    Divider,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import HistoryIcon from '@mui/icons-material/History';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import api from '../../services/api';
import './DecisionTree.css';
import { useNavigation } from '../../contexts/NavigationContext';
import SearchIcon from '@mui/icons-material/Search';
import { useNotification } from '../../contexts/NotificationContext';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Handle, Position } from 'reactflow';
import { DynamicTextProvider, useDynamicText } from '../../contexts/DynamicTextContext';
import { DynamicText } from '../DynamicText/DynamicText';
import HelpIcon from '@mui/icons-material/Help';

// Componente principal que fornece o contexto
const DecisionTreeWrapper = () => {
    return (
        <DynamicTextProvider>
            <DecisionTreeContent />
        </DynamicTextProvider>
    );
};

// Componente que contém a lógica principal
const DecisionTreeContent = () => {
    const { context, updateContext, processText, setMappings } = useDynamicText();
    const theme = useTheme();
    const { navigateTo } = useNavigation();
    const { showError, showSuccess } = useNotification();
    const [currentQuestion, setCurrentQuestion] = useState(null);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [questionStack, setQuestionStack] = useState([]);
    const [selectedAct, setSelectedAct] = useState(null);
    const [selectedFlow, setSelectedFlow] = useState(null);
    const [currentActType, setCurrentActType] = useState(null);
    const [fichaAnchorEl, setFichaAnchorEl] = useState(null);
    const [numeroFicha, setNumeroFicha] = useState('');
    const [fichaError, setFichaError] = useState('');
    const [showFichaInput, setShowFichaInput] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [analysisId, setAnalysisId] = useState(null);
    const [analysisTitle, setAnalysisTitle] = useState('');
    const [jiraIssues, setJiraIssues] = useState([]);
    const [selectedJiraIssue, setSelectedJiraIssue] = useState(null);
    const [jiraDialogOpen, setJiraDialogOpen] = useState(false);
    const [attachToPDF, setAttachToPDF] = useState(true);
    const [isLoadingJiraIssues, setIsLoadingJiraIssues] = useState(false);
    const [searching, setSearching] = useState(false);
    const [generatingPDF, setGeneratingPDF] = useState(false);
    const [flows, setFlows] = useState([]);
    const [idMappingHistory, setIdMappingHistory] = useState([]);
    // Verificar se estamos na página inicial
    const isHomePage = window.location.pathname === '/';

    // Inicialização quando o componente é montado
    useEffect(() => {
        const storedFichaNumber = localStorage.getItem('numeroFicha');
        const storedJiraIssue = localStorage.getItem('selectedJiraIssue');
        
        if (storedFichaNumber) {
            setNumeroFicha(storedFichaNumber);
            console.log('Parâmetros para nova análise carregados:', {
                numeroFicha: storedFichaNumber,
                jiraIssue: storedJiraIssue ? JSON.parse(storedJiraIssue) : null
            });
            
            // Mostrar notificação para o usuário
            showSuccess(`Inicie a análise da ficha ${storedFichaNumber} selecionando um tipo de ato`);
            
            if (storedJiraIssue) {
                setSelectedJiraIssue(JSON.parse(storedJiraIssue));
            }
            
            // Não remover os itens do localStorage ainda, eles serão removidos
            // após o usuário selecionar um fluxo e iniciar a análise
        } else {
            // Se não houver número de ficha no localStorage e não estamos em uma análise salva,
            // redirecionar para a página de histórico apenas se não estivermos na página inicial
            if (!localStorage.getItem('editingAnalysisId') && !isHomePage) {
                console.log('Número de ficha não encontrado, redirecionando para a página de histórico');
                showError('É necessário informar o número da ficha para iniciar uma análise');
                navigateTo('history');
            }
        }

        return () => {
            // Limpar todos os dados de armazenamento ao desmontar o componente
            localStorage.removeItem('editingAnalysisId');
            localStorage.removeItem('numeroFicha');
            localStorage.removeItem('selectedJiraIssue');
            sessionStorage.removeItem('tempFichaNumber');
            console.log('Limpando dados de localStorage e sessionStorage ao desmontar componente');
        };
    }, [navigateTo, showError, showSuccess, isHomePage]);

    // Verificar se o número da ficha está presente quando a página é recarregada
    useEffect(() => {
        // Usar o evento beforeunload para salvar o número da ficha antes da página ser recarregada
        const handleBeforeUnload = () => {
            if (numeroFicha) {
                sessionStorage.setItem('tempFichaNumber', numeroFicha);
            }
        };

        // Verificar primeiro se temos um número de ficha no localStorage (prioridade mais alta)
        const storedFichaNumber = localStorage.getItem('numeroFicha');
        
        if (storedFichaNumber) {
            // Se temos um número no localStorage, usamos ele e ignoramos o sessionStorage
            setNumeroFicha(storedFichaNumber);
            console.log('Usando número da ficha do localStorage (prioridade):', storedFichaNumber);
            // Não removemos do localStorage ainda, isso será feito após selecionar o fluxo
        } else {
            // Se não temos no localStorage, verificamos o sessionStorage
            const tempFichaNumber = sessionStorage.getItem('tempFichaNumber');
            if (tempFichaNumber && !numeroFicha) {
                setNumeroFicha(tempFichaNumber);
                console.log('Recuperando número da ficha do sessionStorage após reload:', tempFichaNumber);
                sessionStorage.removeItem('tempFichaNumber');
            } else if (!numeroFicha && !localStorage.getItem('editingAnalysisId')) {
                // Se não temos número de ficha em nenhum lugar e não estamos editando uma análise existente
                // Evitar redirecionamento infinito verificando se já estamos na página inicial
                if (!isHomePage) {
                    console.log('Número de ficha não encontrado após reload, redirecionando para a página de histórico');
                    showError('É necessário informar o número da ficha para iniciar uma análise');
                    navigateTo('history');
                }
            }
        }

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [numeroFicha, navigateTo, showError, isHomePage]);

    // Persistir o número da ficha no sessionStorage sempre que ele mudar
    useEffect(() => {
        if (numeroFicha) {
            // Verificar se o valor veio do localStorage para evitar sobrescrever
            const storedFichaNumber = localStorage.getItem('numeroFicha');
            if (storedFichaNumber === numeroFicha) {
                // Se o valor é o mesmo do localStorage, não fazemos nada
                console.log('Número da ficha já está no localStorage, não persistindo no sessionStorage');
            } else {
                // Se o valor é diferente ou não existe no localStorage, persistimos no sessionStorage
                sessionStorage.setItem('tempFichaNumber', numeroFicha);
                console.log('Número da ficha persistido no sessionStorage:', numeroFicha);
            }
        }
    }, [numeroFicha]);

    useEffect(() => {
        const loadFlows = async () => {
            try {
                console.log('Iniciando carregamento de fluxos...');
                // Usar o endpoint do novo modelo de fluxo
                const response = await api.get('/flows-new');
                console.log('Resposta da API de fluxos:', response.data);
                
                // Filtra apenas os fluxos ativos e disponíveis
                const activeFlows = response.data.filter(flow => {
                    console.log(`Fluxo ${flow.id} - ${flow.name}:`, {
                        is_active: flow.is_active,
                        available: flow.available,
                        tipo_is_active: typeof flow.is_active,
                        tipo_available: typeof flow.available
                    });
                    
                    // Considera available como true se for undefined
                    const isActive = flow.is_active === true;
                    const isAvailable = flow.available === undefined ? true : flow.available === true;
                    
                    console.log(`Fluxo ${flow.id} - ${flow.name}: isActive=${isActive}, isAvailable=${isAvailable}, será incluído=${isActive && isAvailable}`);
                    
                    return isActive && isAvailable;
                });
                
                console.log('Fluxos ativos e disponíveis após filtro:', activeFlows);
                
                // Ordenar fluxos alfabeticamente pelo nome
                const sortedFlows = [...activeFlows].sort((a, b) => {
                    if (!a.name) return 1;
                    if (!b.name) return -1;
                    return a.name.localeCompare(b.name, 'pt-BR');
                });
                
                console.log('Fluxos ordenados alfabeticamente:', sortedFlows);
                setFlows(sortedFlows);
            } catch (error) {
                console.error('Erro ao carregar fluxos:', error);
                showError('Erro ao carregar os fluxos disponíveis');
            }
        };
        loadFlows();
    }, []);

    const fetchFirstQuestion = async () => {
        try {
            if (!selectedFlow || !selectedFlow.id) {
                throw new Error('Nenhum fluxo selecionado');
            }
            
            // Usar o endpoint do novo modelo de fluxo
            const response = await api.get(`/flows-new/${selectedFlow.id}/questions/first`);
            setCurrentQuestion(response.data);
        } catch (error) {
            console.error('Erro:', error);
            throw error;
        }
    };

    const searchJiraIssues = async (searchTerm) => {
        try {
            setIsLoadingJiraIssues(true);
            const response = await api.get(`/settings/jira/issues/${searchTerm}`);
            setJiraIssues(response.data);
        } catch (error) {
            console.error('Erro ao buscar issues do JIRA:', error);
            setSnackbarMessage('Erro ao buscar issues do JIRA');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsLoadingJiraIssues(false);
        }
    };

    const handleStartAnalysis = async () => {
        try {
            setLoading(true);
            await fetchFirstQuestion();
        } catch (error) {
            console.error('Erro ao iniciar análise:', error);
            setError('Erro ao iniciar análise. Por favor, tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        window.scrollTo({
            top: document.documentElement.scrollHeight,
            behavior: 'smooth'
        });
    };

    // Atualiza o contexto quando uma resposta é selecionada
    const handleOptionSelect = async (optionIndex, customOption = null) => {
        try {
            if (!currentQuestion) return;

            const selectedAnswer = customOption || currentQuestion.options[optionIndex];
            
            // Criar ou atualizar o mapeamento de IDs de questões
            let questionBankMapping = {};
            if (context.flowContext?.questionBankMapping) {
                questionBankMapping = { ...context.flowContext.questionBankMapping };
            }
            
            // Adicionar o mapeamento atual
            if (currentQuestion.id && currentQuestion.question_bank_id) {
                questionBankMapping[currentQuestion.id] = currentQuestion.question_bank_id;
            }
            
            // Atualiza o histórico de respostas no contexto
            console.log('Atualizando contexto com questionBankMapping:', questionBankMapping);

            updateContext({
                previousAnswers: [
                    ...history.map(h => `${h.questionBankId}:${h.answer}`),
                    `${currentQuestion.question_bank_id}:${selectedAnswer}`
                ],
                flowContext: {
                    currentQuestionId: currentQuestion.id,
                    flowId: selectedFlow?.id,
                    questionBankMapping: questionBankMapping
                }
            });

            setQuestionStack(prev => [...prev, currentQuestion]);
            
            // Processa o texto das perguntas e respostas antes de adicionar ao histórico
            setHistory(prev => [...prev, {
                question: currentQuestion.text,
                answer: selectedAnswer,
                article_reference: currentQuestion.article_reference,
                questionId: currentQuestion.id, // Armazenar o ID da questão no histórico
                questionBankId: currentQuestion.question_bank_id, // Armazenar o ID do banco de questões
                // Mantém as versões originais para possíveis usos futuros
                originalQuestion: currentQuestion.text,
                originalAnswer: selectedAnswer
            }]);

            if (currentQuestion.is_final) {
                setCurrentQuestion({
                    ...currentQuestion,
                    showFinalMessage: true
                });
                setTimeout(scrollToBottom, 100);
                return;
            }

            try {
                const response = await api.post(`/flows-new/${selectedFlow.id}/navigate`, {
                    flow_question_id: currentQuestion.id,
                    selected_option: optionIndex
                });

                if (response.data.dynamic_text) {
                    setMappings(response.data.dynamic_text);
                }

                setCurrentQuestion(response.data);
                setError(null);
                setTimeout(scrollToBottom, 100);
            } catch (error) {
                console.error('Erro ao navegar:', error);
                if (error.response && error.response.status === 404) {
                    setError('Não foi possível encontrar a próxima questão.');
                } else {
                    setError('Ocorreu um erro ao processar sua resposta.');
                }
            }
        } catch (error) {
            console.error('Erro ao selecionar opção:', error);
            setError('Ocorreu um erro ao processar sua resposta.');
        }
    };

    const handleBack = () => {
        if (questionStack.length > 0) {
            // Remove a última questão da pilha e a define como atual
            const previousQuestions = [...questionStack];
            const lastQuestion = previousQuestions.pop();
            setQuestionStack(previousQuestions);
            setCurrentQuestion(lastQuestion);
            
            // Remove a última resposta do histórico
            const newHistory = history.slice(0, -1);
            setHistory(newHistory);

            // Se voltou para antes da seleção do tipo de ato, reseta o tipo
            const firstQuestionIndex = newHistory.findIndex(h => h.question === "Qual o tipo de ato a ser realizado?");
            if (firstQuestionIndex === -1) {
                setCurrentActType(null);
            }
        }
    };

    const handleReset = () => {
        setHistory([]);
        setCurrentQuestion(null);
        setQuestionStack([]);
        setCurrentActType(null);
        setSelectedAct(null);
        setShowFichaInput(false);
        setAnalysisId(null);
        setAnalysisTitle('');
        setSelectedFlow(null);
    };

    const handleOpenFichaPopover = (event) => {
        setFichaAnchorEl(event.currentTarget);
        setFichaError('');
        setJiraIssues([]);
        setSelectedJiraIssue(null);
    };

    const handleCloseFichaPopover = () => {
        setFichaAnchorEl(null);
        setNumeroFicha('');
    };

    const formatContent = () => {
        let content = '';
        
        // Adiciona o histórico de perguntas e respostas
        history.forEach((item, index) => {
            content += `Pergunta ${index + 1}: ${item.question}\n`;
            content += `Resposta: ${item.answer}\n`;
            if (item.article_reference) {
                content += `Referência: ${item.article_reference}\n`;
            }
            content += '\n';
        });

        // Adiciona a conclusão final
        if (currentQuestion && currentQuestion.final_message) {
            content += `Conclusão: ${processText(currentQuestion.final_message)}\n`;
            if (currentQuestion.article_reference) {
                content += `Referência final: ${currentQuestion.article_reference}\n`;
            }
        }

        return content;
    };

    const handleJiraIssueSelect = async () => {
        setJiraDialogOpen(false);
        if (selectedJiraIssue) {
            setAnalysisTitle(`${numeroFicha} - ${selectedJiraIssue.fields.summary}`);
        }
    };

    const handleGeneratePDF = async (analysisId) => {
        try {
            setGeneratingPDF(true);
            
            // Atualiza a URL para incluir o prefixo /api/v1
            const response = await api.get(`/pdf/analysis/${analysisId}`, {
                responseType: 'blob',
                headers: {
                    'Accept': 'application/pdf'
                }
            });

            if (response.status === 404) {
                throw new Error('Análise não encontrada');
            }

            const blob = new Blob([response.data], { type: 'application/pdf' });
            if (blob.size === 0) {
                throw new Error('O PDF gerado está vazio. Por favor, tente novamente.');
            }

            // Cria um arquivo a partir do blob
            const file = new File([blob], `analise-ficha-${numeroFicha}.pdf`, { type: 'application/pdf' });

            // Se tiver uma issue do JIRA selecionada e a opção de anexar estiver marcada
            if (selectedJiraIssue && attachToPDF) {
                const formData = new FormData();
                formData.append('file_path', file);
                formData.append('file_name', `analise-ficha-${numeroFicha}.pdf`);
                formData.append('analysis_id', analysisId);

                // Envia o arquivo para ser anexado ao JIRA
                await api.post(
                    `/settings/jira/issues/${selectedJiraIssue.key}/attachments`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );

                setSnackbarMessage('PDF gerado no novo formato e anexado ao JIRA com sucesso!');
            } else {
                setSnackbarMessage('PDF gerado no novo formato com sucesso!');
            }

            // Download do arquivo
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `analise-ficha-${numeroFicha}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erro ao gerar PDF:', error);
            setSnackbarMessage(
                error.response?.status === 404
                    ? 'Análise não encontrada. Por favor, tente novamente.'
                    : error.response?.status === 403
                    ? 'Você não tem permissão para gerar o PDF desta análise.'
                    : `Erro ao gerar o PDF: ${error.message}`
            );
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setGeneratingPDF(false);
        }
    };

    const handleSaveAnalysis = async () => {
        try {
            setLoading(true);
            
            // Obtém o tipo de ato da primeira pergunta
            const actType = history[0]?.answer || 'Não especificado';
            
            const analysisData = {
                title: `Análise da Ficha ${numeroFicha}`,
                content: formatContent(),
                ficha_number: numeroFicha,
                act_type: actType,
                jira_issue_key: selectedJiraIssue?.key || null,
                competent: currentQuestion?.is_success
            };

            console.log('Salvando análise com dados:', analysisData);
            const response = await api.post('/analyses', analysisData);
            setAnalysisId(response.data.id);
            setSnackbarMessage('Análise salva com sucesso!');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
        } catch (error) {
            console.error('Erro ao salvar análise:', error);
            if (error.response?.status === 403) {
                setSnackbarMessage('Sessão expirada. Por favor, faça login novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
                navigateTo('login');
            } else {
                setError('Erro ao salvar a análise');
                setSnackbarMessage('Erro ao salvar a análise. Por favor, tente novamente.');
                setSnackbarSeverity('error');
                setSnackbarOpen(true);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleHistoryClick = (index) => {
        // Confirma com o usuário antes de voltar
        const confirmReturn = window.confirm('Deseja voltar para esta questão? Todas as respostas posteriores serão descartadas.');
        
        if (confirmReturn) {
            // Verifica se está voltando para a primeira pergunta
            const isFirstQuestion = history[index].question === "Qual o tipo de ato a ser realizado?";
            
            if (isFirstQuestion) {
                // Se for a primeira pergunta, limpa todo o histórico e reinicia
                setHistory([]);
                setQuestionStack([]);
                setCurrentActType(null);
                fetchFirstQuestion();
                return;
            }

            // Mantém o histórico até o índice selecionado
            const newHistory = history.slice(0, index + 1);
            setHistory(newHistory);
            
            // Atualiza a pilha de questões
            const newQuestionStack = questionStack.slice(0, index + 1);
            setQuestionStack(newQuestionStack);
            
            // Define a questão atual como a última do stack
            const lastQuestion = questionStack[index];
            setCurrentQuestion(lastQuestion);

            // Se voltou para antes da seleção do tipo de ato, reseta o tipo
            const firstQuestionIndex = newHistory.findIndex(h => h.question === "Qual o tipo de ato a ser realizado?");
            if (firstQuestionIndex === -1 || index < firstQuestionIndex) {
                setCurrentActType(null);
            } else if (firstQuestionIndex === index) {
                // Se voltou para a seleção do tipo de ato, atualiza o tipo
                const selectedOption = newHistory[firstQuestionIndex].answer;
                setCurrentActType(firstQuestionIndex);
            }
        }
    };

    // Atualiza o contexto quando o fluxo é selecionado
    const handleFlowSelect = async (flow) => {
        console.log('handleFlowSelect chamado com fluxo:', flow);
        
        // Verificar se temos um número de ficha armazenado no localStorage ou no estado
        const storedFichaNumber = localStorage.getItem('numeroFicha');
        const currentFichaNumber = numeroFicha;
        
        // Se não temos número de ficha em nenhum lugar e não estamos editando uma análise existente
        if (!storedFichaNumber && !currentFichaNumber && !localStorage.getItem('editingAnalysisId')) {
            // Evitar redirecionamento infinito verificando se já estamos na página inicial
            if (!isHomePage) {
                console.log('Número de ficha não encontrado ao selecionar fluxo, redirecionando para a página de histórico');
                showError('É necessário informar o número da ficha para iniciar uma análise');
                navigateTo('history');
                return;
            }
        }
        
        setSelectedFlow(flow);
        
        // Limpar o contexto anterior
        updateContext({
            actType: flow.name,
            previousAnswers: [],
            flowContext: {
                flowId: flow.id,
                questionOrder: [],
                questionBankMapping: {} // Inicializar o mapeamento vazio
            }
        });
        
        try {
            setLoading(true);
            
            // Verificar se temos um número de ficha armazenado no localStorage
            const storedJiraIssue = localStorage.getItem('selectedJiraIssue');
            
            console.log('Verificando localStorage na seleção de fluxo:', {
                storedFichaNumber,
                storedJiraIssue: storedJiraIssue ? 'Presente' : 'Ausente'
            });
            
            // Adiciona o tipo de ato ao histórico
            setHistory(prev => [...prev, {
                question: "Tipo de Ato Selecionado",
                answer: flow.name,
                article_reference: null
            }]);
            
            const response = await api.get(`/flows-new/${flow.id}/questions/first`);
            console.log('Primeira questão carregada:', response.data);
            
            if (response.data.dynamic_text) {
                setMappings(response.data.dynamic_text);
            }
            
            setCurrentQuestion(response.data);
            
            // Inicializar o mapeamento com a primeira questão
            const questionBankMapping = {};
            if (response.data.id && response.data.question_bank_id) {
                questionBankMapping[response.data.id] = response.data.question_bank_id;
            }
            
            // Atualizar o contexto com o ID da primeira questão
            if (response.data.id) {
                updateContext({
                    flowContext: {
                        currentQuestionId: response.data.id,
                        flowId: flow.id,
                        questionBankMapping: questionBankMapping
                    }
                });
            }
            
            // Se temos um número de ficha armazenado no localStorage,
            // estamos iniciando uma nova análise a partir da página de histórico
            if (storedFichaNumber) {
                console.log('Iniciando nova análise com ficha:', storedFichaNumber);
                // Define o número da ficha no estado
                setNumeroFicha(storedFichaNumber);
                
                // Mostra uma notificação de sucesso
                showSuccess(`Análise da ficha ${storedFichaNumber} iniciada com o tipo de ato: ${flow.name}`);
                
                // Limpa os dados do localStorage e sessionStorage após iniciar a análise com sucesso
                console.log('Limpando dados do localStorage e sessionStorage após iniciar análise');
                localStorage.removeItem('numeroFicha');
                localStorage.removeItem('selectedJiraIssue');
                sessionStorage.removeItem('tempFichaNumber');
            }
        } catch (error) {
            console.error('Erro ao carregar primeira pergunta:', error);
            showError('Erro ao iniciar o fluxo selecionado');
        } finally {
            setLoading(false);
        }
    };

    const renderFlowSelection = () => {
        // Verifica se há um número de ficha no localStorage ou no estado
        const storedFichaNumber = localStorage.getItem('numeroFicha');
        const hasFichaNumber = storedFichaNumber || numeroFicha;
        
        if (!flows.length) {
            return (
                <Box sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    width: '100%'
                }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        color="primary"
                        sx={{ 
                            fontWeight: 600,
                            textAlign: 'center'
                        }}
                    >
                        Nenhum Tipo de Ato Disponível
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.4)'
                                : 'rgba(129, 0, 0, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 2,
                            maxWidth: '600px',
                            width: '100%'
                        }}
                    >
                        <Typography 
                            variant="body1"
                            sx={{ 
                                color: theme.palette.text.primary,
                                lineHeight: 1.8,
                                textAlign: 'center'
                            }}
                        >
                            Não há tipos de atos disponíveis para seleção no momento. Por favor, entre em contato com o administrador do sistema.
                        </Typography>
                    </Paper>
                    <InfoIcon color="info" sx={{ fontSize: 40, opacity: 0.7, mt: 2 }} />
                </Box>
            );
        }

        return (
            <Box sx={{ 
                p: { xs: 2, sm: 3, md: 4 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
                width: '100%',
                maxWidth: '800px',
                mx: 'auto'
            }}>
                {!hasFichaNumber && !localStorage.getItem('editingAnalysisId') && !isHomePage && (
                    <Alert 
                        severity="warning" 
                        variant="filled"
                        icon={<InfoIcon />}
                        sx={{ 
                            width: '100%', 
                            maxWidth: '600px', 
                            mb: 2,
                            fontSize: '1.1rem',
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.5rem'
                            }
                        }}
                        action={
                            <Button 
                                color="inherit" 
                                size="small" 
                                onClick={() => navigateTo('history')}
                                sx={{ fontWeight: 'bold' }}
                            >
                                Ir para Histórico
                            </Button>
                        }
                    >
                        É necessário informar o número da ficha para iniciar uma análise.
                    </Alert>
                )}

                <Typography 
                    variant="h5" 
                    gutterBottom 
                    color="primary"
                    sx={{ 
                        fontWeight: 600,
                        textAlign: 'center',
                        mb: 3
                    }}
                >
                    {hasFichaNumber ? 'Selecione o Tipo de Ato para a Ficha ' + (numeroFicha || storedFichaNumber) : 'Selecione o Tipo de Ato'}
                </Typography>

                <Autocomplete
                    value={selectedFlow}
                    onChange={(event, newValue) => {
                        if (newValue) {
                            console.log('Fluxo selecionado:', newValue);
                            handleFlowSelect(newValue);
                        }
                    }}
                    options={flows}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Tipo de Ato"
                            variant="outlined"
                            placeholder="Selecione ou digite para buscar um tipo de ato"
                            sx={{
                                width: '100%',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    transition: 'all 0.2s',
                                    borderColor: hasFichaNumber ? theme.palette.primary.main : undefined,
                                    backgroundColor: theme.palette.mode === 'dark' 
                                        ? alpha(theme.palette.background.paper, 0.6)
                                        : alpha(theme.palette.background.paper, 0.9),
                                    boxShadow: hasFichaNumber 
                                        ? `0 0 8px ${alpha(theme.palette.primary.main, 0.3)}` 
                                        : theme.shadows[1],
                                    '&:hover': {
                                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: theme.shadows[3]
                                    },
                                    '&.Mui-focused': {
                                        borderColor: theme.palette.primary.main,
                                        boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                    }
                                }
                            }}
                        />
                    )}
                    disabled={loading || (!hasFichaNumber && !localStorage.getItem('editingAnalysisId') && !isHomePage)}
                    sx={{
                        width: '100%',
                        maxWidth: '600px',
                        '& .MuiAutocomplete-paper': {
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: theme.shadows[8],
                            borderRadius: 2
                        }
                    }}
                />
                
                {hasFichaNumber && (
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                            mt: 2, 
                            textAlign: 'center',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5
                        }}
                    >
                        <InfoIcon fontSize="small" color="info" />
                        Selecione um tipo de ato acima para iniciar a análise da ficha {numeroFicha || storedFichaNumber}
                    </Typography>
                )}
                
                {!hasFichaNumber && !localStorage.getItem('editingAnalysisId') && !isHomePage && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigateTo('history')}
                        sx={{ 
                            mt: 3,
                            px: 4,
                            py: 1.5,
                            borderRadius: 2
                        }}
                    >
                        Ir para o Histórico
                    </Button>
                )}
            </Box>
        );
    };

    const renderQuestionContent = () => {
        if (!flows.length) {
            return (
                <Box sx={{ 
                    p: 4, 
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                    width: '100%'
                }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        color="primary"
                        sx={{ 
                            fontWeight: 600,
                            textAlign: 'center'
                        }}
                    >
                        Nenhum Tipo de Ato Disponível
                    </Typography>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.4)'
                                : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : 'rgba(0, 0, 0, 0.1)',
                            borderRadius: 2,
                            maxWidth: '600px',
                            width: '100%'
                        }}
                    >
                        <Typography 
                            variant="body1"
                            sx={{ 
                                color: theme.palette.text.primary,
                                lineHeight: 1.8,
                                textAlign: 'center'
                            }}
                        >
                            Não há tipos de atos disponíveis para seleção no momento. Por favor, entre em contato com o administrador do sistema.
                        </Typography>
                    </Paper>
                    <InfoIcon color="info" sx={{ fontSize: 40, opacity: 0.7, mt: 2 }} />
                </Box>
            );
        }

        if (!selectedFlow) {
            return renderFlowSelection();
        }

        if (!currentQuestion) {
            return (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (currentQuestion.showFinalMessage || (currentQuestion.is_final && currentQuestion.options.length === 0)) {
            return (
                <Box sx={{ 
                    background: currentQuestion.is_success === true
                        ? theme.palette.mode === 'dark'
                            ? 'linear-gradient(135deg, rgba(46, 125, 50, 0.3) 0%, rgba(46, 125, 50, 0.1) 100%)'
                            : 'linear-gradient(135deg, rgba(46, 125, 50, 0.15) 0%, rgba(76, 175, 80, 0.15) 100%)'
                        : currentQuestion.is_success === false
                            ? theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(198, 40, 40, 0.3) 0%, rgba(198, 40, 40, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(211, 47, 47, 0.15) 0%, rgba(239, 83, 80, 0.15) 100%)'
                            : theme.palette.mode === 'dark'
                                ? 'linear-gradient(135deg, rgba(25, 118, 210, 0.3) 0%, rgba(25, 118, 210, 0.1) 100%)'
                                : 'linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(33, 150, 243, 0.15) 100%)',
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid',
                    borderColor: currentQuestion.is_success === true
                        ? theme.palette.mode === 'dark'
                            ? 'rgba(76, 175, 80, 0.4)'
                            : 'rgba(76, 175, 80, 0.2)'
                        : currentQuestion.is_success === false
                            ? theme.palette.mode === 'dark'
                                ? 'rgba(239, 83, 80, 0.4)'
                                : 'rgba(239, 83, 80, 0.2)'
                            : theme.palette.mode === 'dark'
                                ? 'rgba(33, 150, 243, 0.4)'
                                : 'rgba(33, 150, 243, 0.2)',
                    p: 3
                }}>
                    <Typography 
                        variant="h5" 
                        gutterBottom 
                        sx={{ 
                            color: currentQuestion.is_success === true
                                ? theme.palette.mode === 'dark' ? '#81C784' : '#2E7D32'
                                : currentQuestion.is_success === false
                                    ? theme.palette.mode === 'dark' ? '#FF8A80' : '#C62828'
                                    : theme.palette.mode === 'dark' ? '#64B5F6' : '#1565C0',
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 3
                        }}
                    >
                        {currentQuestion.is_success === true ? '✅ Conclusão Positiva' : 
                         currentQuestion.is_success === false ? '❌ Conclusão Negativa' : 
                         'ℹ️ Conclusão'}
                    </Typography>

                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            backgroundColor: theme.palette.mode === 'dark'
                                ? 'rgba(0, 0, 0, 0.4)'
                                : 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'dark'
                                ? currentQuestion.is_success === true
                                    ? 'rgba(76, 175, 80, 0.4)'
                                    : currentQuestion.is_success === false
                                        ? 'rgba(239, 83, 80, 0.4)'
                                        : 'rgba(33, 150, 243, 0.4)'
                                : currentQuestion.is_success === true
                                    ? 'rgba(76, 175, 80, 0.2)'
                                    : currentQuestion.is_success === false
                                        ? 'rgba(239, 83, 80, 0.2)'
                                        : 'rgba(33, 150, 243, 0.2)',
                            borderRadius: 2,
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: 6,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? currentQuestion.is_success === true
                                        ? '#2E7D32'
                                        : currentQuestion.is_success === false
                                            ? '#C62828'
                                            : '#1565C0'
                                    : currentQuestion.is_success === true
                                        ? '#2E7D32'
                                        : currentQuestion.is_success === false
                                            ? '#C62828'
                                            : '#1565C0',
                                borderRadius: '4px 0 0 4px'
                            }
                        }}
                    >
                        <Typography 
                            variant="subtitle1" 
                            sx={{ 
                                color: theme.palette.mode === 'dark'
                                    ? currentQuestion.is_success === true
                                        ? '#81C784'
                                        : currentQuestion.is_success === false
                                            ? '#FF8A80'
                                            : '#64B5F6'
                                    : currentQuestion.is_success === true
                                        ? '#2E7D32'
                                        : currentQuestion.is_success === false
                                            ? '#C62828'
                                            : '#1565C0',
                                fontWeight: 600,
                                mb: 2
                            }}
                        >
                            Conclusão:
                        </Typography>
                        <Typography 
                            variant="body1"
                            sx={{ 
                                color: theme.palette.mode === 'dark'
                                    ? currentQuestion.is_success === true
                                        ? '#A5D6A7'
                                        : currentQuestion.is_success === false
                                            ? '#FFCDD2'
                                            : '#90CAF9'
                                    : currentQuestion.is_success === true
                                        ? '#1B5E20'
                                        : currentQuestion.is_success === false
                                            ? '#B71C1C'
                                            : '#0D47A1',
                                lineHeight: 1.8,
                                fontSize: '1.1rem',
                                letterSpacing: '0.015em'
                            }}
                        >
                            <DynamicText text={currentQuestion.final_message} />
                        </Typography>
                    </Paper>

                    {currentQuestion.article_reference && (
                        <Paper 
                            elevation={0}
                            className="legal-reference"
                            sx={{ 
                                p: 3,
                                mt: 3,
                                backgroundColor: theme.palette.mode === 'dark'
                                    ? 'rgba(0, 0, 0, 0.4)'
                                    : 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid',
                                borderColor: theme.palette.mode === 'dark'
                                    ? currentQuestion.is_success === true
                                        ? 'rgba(76, 175, 80, 0.4)'
                                        : currentQuestion.is_success === false
                                            ? 'rgba(239, 83, 80, 0.4)'
                                            : 'rgba(33, 150, 243, 0.4)'
                                    : currentQuestion.is_success === true
                                        ? 'rgba(76, 175, 80, 0.2)'
                                        : currentQuestion.is_success === false
                                            ? 'rgba(239, 83, 80, 0.2)'
                                            : 'rgba(33, 150, 243, 0.2)',
                                borderRadius: 2,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: 6,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? currentQuestion.is_success === true
                                            ? '#2E7D32'
                                            : currentQuestion.is_success === false
                                                ? '#C62828'
                                                : '#1565C0'
                                        : currentQuestion.is_success === true
                                            ? '#2E7D32'
                                            : currentQuestion.is_success === false
                                                ? '#C62828'
                                                : '#1565C0',
                                    borderRadius: '4px 0 0 4px'
                                }
                            }}
                        >
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    color: theme.palette.mode === 'dark'
                                        ? '#64B5F6'
                                        : '#1565C0',
                                    fontWeight: 600,
                                    mb: 2
                                }}
                            >
                                Fundamentação legal:
                            </Typography>
                            <Typography 
                                variant="body1"
                                sx={{ 
                                    color: theme.palette.mode === 'dark'
                                        ? '#90CAF9'
                                        : '#1976D2',
                                    lineHeight: 1.6
                                }}
                            >
                                {currentQuestion.article_reference}
                            </Typography>
                        </Paper>
                    )}

                    <Stack 
                        direction="row" 
                        justifyContent="center" 
                        spacing={2} 
                        sx={{ mt: 3 }}
                    >
                        {!analysisId ? (
                            <Button
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveAnalysis}
                                disabled={loading}
                                sx={{
                                    py: 1.5,
                                    px: 4,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? currentQuestion.is_success === true
                                            ? alpha(theme.palette.success.dark, 0.8)
                                            : currentQuestion.is_success === false
                                                ? alpha(theme.palette.error.dark, 0.8)
                                                : alpha(theme.palette.primary.dark, 0.8)
                                        : currentQuestion.is_success === true
                                            ? theme.palette.success.main
                                            : currentQuestion.is_success === false
                                                ? theme.palette.error.main
                                                : theme.palette.primary.main,
                                    color: '#fff',
                                    border: 'none',
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                        : currentQuestion.is_success === true
                                            ? '0 2px 8px rgba(46, 125, 50, 0.3)'
                                            : currentQuestion.is_success === false
                                                ? '0 2px 8px rgba(211, 47, 47, 0.3)'
                                                : '0 2px 8px rgba(33, 150, 243, 0.3)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? currentQuestion.is_success === true
                                                ? theme.palette.success.dark
                                                : currentQuestion.is_success === false
                                                    ? theme.palette.error.dark
                                                    : theme.palette.primary.dark
                                            : currentQuestion.is_success === true
                                                ? alpha(theme.palette.success.dark, 0.9)
                                                : currentQuestion.is_success === false
                                                    ? alpha(theme.palette.error.dark, 0.9)
                                                    : alpha(theme.palette.primary.dark, 0.9),
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                            : currentQuestion.is_success === true
                                                ? '0 4px 12px rgba(46, 125, 50, 0.4)'
                                                : currentQuestion.is_success === false
                                                    ? '0 4px 12px rgba(211, 47, 47, 0.4)'
                                                    : '0 4px 12px rgba(33, 150, 243, 0.4)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.action.disabled, 0.3)
                                            : alpha(theme.palette.action.disabled, 0.2),
                                        color: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.common.white, 0.4)
                                            : alpha(theme.palette.common.white, 0.6)
                                    }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Salvar Análise'}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<PictureAsPdfIcon />}
                                onClick={() => handleGeneratePDF(analysisId)}
                                disabled={generatingPDF}
                                sx={{
                                    py: 1.5,
                                    px: 4,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '1rem',
                                    fontWeight: 500,
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? currentQuestion.is_success === true
                                            ? alpha(theme.palette.success.dark, 0.8)
                                            : currentQuestion.is_success === false
                                                ? alpha(theme.palette.error.dark, 0.8)
                                                : alpha(theme.palette.primary.dark, 0.8)
                                        : currentQuestion.is_success === true
                                            ? theme.palette.success.main
                                            : currentQuestion.is_success === false
                                                ? theme.palette.error.main
                                                : theme.palette.primary.main,
                                    color: '#fff',
                                    border: 'none',
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                        : currentQuestion.is_success === true
                                            ? '0 2px 8px rgba(46, 125, 50, 0.3)'
                                            : currentQuestion.is_success === false
                                                ? '0 2px 8px rgba(211, 47, 47, 0.3)'
                                                : '0 2px 8px rgba(33, 150, 243, 0.3)',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? currentQuestion.is_success === true
                                                ? theme.palette.success.dark
                                                : currentQuestion.is_success === false
                                                    ? theme.palette.error.dark
                                                    : theme.palette.primary.dark
                                            : currentQuestion.is_success === true
                                                ? alpha(theme.palette.success.dark, 0.9)
                                                : currentQuestion.is_success === false
                                                    ? alpha(theme.palette.error.dark, 0.9)
                                                    : alpha(theme.palette.primary.dark, 0.9),
                                        transform: 'translateY(-2px)',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                            : currentQuestion.is_success === true
                                                ? '0 4px 12px rgba(46, 125, 50, 0.4)'
                                                : currentQuestion.is_success === false
                                                    ? '0 4px 12px rgba(211, 47, 47, 0.4)'
                                                    : '0 4px 12px rgba(33, 150, 243, 0.4)'
                                    },
                                    '&:disabled': {
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.action.disabled, 0.3)
                                            : alpha(theme.palette.action.disabled, 0.2),
                                        color: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.common.white, 0.4)
                                            : alpha(theme.palette.common.white, 0.6)
                                    }
                                }}
                            >
                                {generatingPDF ? <CircularProgress size={24} color="inherit" /> : 'Gerar PDF'}
                            </Button>
                        )}
                    </Stack>
                </Box>
            );
        }

        return (
            <Box sx={{ p: 2 }}>
                <Typography 
                    variant="h5" 
                    gutterBottom 
                    color="primary"
                    sx={{ 
                        fontWeight: 600,
                        mb: 3,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        fontSize: '1.25rem !important',
                        '& .MuiTypography-root': {
                            fontWeight: 600,
                            fontSize: '1.25rem !important'
                        }
                    }}
                >
                    <HelpIcon color="primary" fontSize="medium" />
                    {currentQuestion.is_final ? (
                        <DynamicText 
                            text={currentQuestion.final_message} 
                            variant="h5"
                            fontWeight={600}
                            fontSize="1.25rem"
                        />
                    ) : (
                        <DynamicText 
                            text={currentQuestion.text} 
                            variant="h5"
                            fontWeight={600}
                            fontSize="1.25rem"
                        />
                    )}
                </Typography>
                <Box sx={{ mt: 3 }}>
                    {currentQuestion?.text === "Qual o tipo de ato a ser realizado?" ? (
                        <Autocomplete
                            value={selectedAct}
                            onChange={(event, newValue) => {
                                setSelectedAct(newValue);
                                if (newValue) {
                                    // Encontra o índice original da opção selecionada
                                    const originalIndex = currentQuestion.options.indexOf(newValue);
                                    handleOptionSelect(originalIndex, newValue);
                                }
                            }}
                            options={[...currentQuestion.options]}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Selecione ou digite para buscar"
                                    variant="outlined"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            transition: 'all 0.2s',
                                            backgroundColor: theme.palette.mode === 'dark' 
                                                ? alpha(theme.palette.background.paper, 0.15)
                                                : alpha(theme.palette.background.paper, 0.8),
                                            borderColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.3)
                                                : alpha(theme.palette.primary.main, 0.2),
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 2px 8px rgba(0, 0, 0, 0.2)'
                                                : '0 2px 8px rgba(0, 0, 0, 0.05)',
                                            '&:hover': {
                                                backgroundColor: theme.palette.mode === 'dark' 
                                                    ? alpha(theme.palette.background.paper, 0.2)
                                                    : alpha(theme.palette.background.paper, 0.9),
                                                borderColor: theme.palette.mode === 'dark'
                                                    ? alpha(theme.palette.primary.main, 0.5)
                                                    : alpha(theme.palette.primary.main, 0.3),
                                                boxShadow: theme.palette.mode === 'dark'
                                                    ? '0 4px 12px rgba(0, 0, 0, 0.3)'
                                                    : '0 4px 12px rgba(0, 0, 0, 0.1)'
                                            },
                                            '&.Mui-focused': {
                                                borderColor: theme.palette.primary.main,
                                                boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                                            }
                                        },
                                        '& .MuiInputLabel-root': {
                                            color: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.text.primary, 0.7)
                                                : alpha(theme.palette.text.primary, 0.6),
                                            '&.Mui-focused': {
                                                color: theme.palette.primary.main
                                            }
                                        },
                                        '& .MuiAutocomplete-input': {
                                            color: theme.palette.text.primary
                                        }
                                    }}
                                />
                            )}
                            disabled={loading}
                            sx={{ 
                                mb: 2,
                                '& .MuiAutocomplete-paper': {
                                    backgroundColor: theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.background.paper, 0.9)
                                        : theme.palette.background.paper,
                                    boxShadow: theme.palette.mode === 'dark'
                                        ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                                        : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    borderRadius: 2
                                }
                            }}
                        />
                    ) : (
                        <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 2,
                            maxWidth: 'sm',
                            mx: 'auto'
                        }}>
                            {currentQuestion.options.map((option, index) => (
                                <Button
                                    key={index}
                                    variant="outlined"
                                    onClick={() => handleOptionSelect(index)}
                                    disabled={loading}
                                    sx={{
                                        py: 1.5,
                                        px: 3,
                                        justifyContent: 'center', // Centraliza o texto horizontalmente
                                        textAlign: 'center', // Garante que o texto esteja centralizado
                                        borderRadius: 2,
                                        minWidth: '160px',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        backgroundColor: theme.palette.mode === 'dark' 
                                            ? alpha(theme.palette.primary.main, 0.15)
                                            : alpha(theme.palette.primary.main, 0.08),
                                        color: theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                        border: '1px solid',
                                        borderColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.main, 0.3)
                                            : alpha(theme.palette.primary.main, 0.2),
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                            : '0 2px 8px rgba(0, 0, 0, 0.05)',
                                        transition: 'all 0.2s ease',
                                        whiteSpace: 'normal', // Permite quebra de linha para textos longos
                                        lineHeight: 1.4, // Melhora o espaçamento entre linhas
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.25)
                                                : alpha(theme.palette.primary.main, 0.15),
                                            borderColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.5)
                                                : alpha(theme.palette.primary.main, 0.3),
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                                : '0 4px 12px rgba(33, 150, 243, 0.15)'
                                        },
                                        '&:disabled': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.background.paper, 0.1)
                                                : alpha(theme.palette.background.paper, 0.3),
                                            color: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.text.primary, 0.3)
                                                : alpha(theme.palette.text.primary, 0.4),
                                            borderColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.divider, 0.1)
                                                : alpha(theme.palette.divider, 0.2)
                                        }
                                    }}
                                >
                                    {option}
                                </Button>
                            ))}
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    const renderJiraDialog = () => (
        <Dialog open={jiraDialogOpen} onClose={() => setJiraDialogOpen(false)}>
            <DialogTitle>Selecione a Issue do JIRA</DialogTitle>
            <DialogContent>
                <Box sx={{ minWidth: 400, mt: 2 }}>
                    {isLoadingJiraIssues ? (
                        <CircularProgress />
                    ) : (
                        <Autocomplete
                            options={jiraIssues}
                            getOptionLabel={(option) => `${option.key} - ${option.fields.summary}`}
                            value={selectedJiraIssue}
                            onChange={(event, newValue) => setSelectedJiraIssue(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Issue do JIRA"
                                    variant="outlined"
                                />
                            )}
                        />
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setJiraDialogOpen(false)}>Cancelar</Button>
                <Button 
                    onClick={handleJiraIssueSelect}
                    disabled={!selectedJiraIssue}
                    variant="contained"
                >
                    Continuar
                </Button>
            </DialogActions>
        </Dialog>
    );

    const renderPDFPopover = () => (
        <Popover
            open={Boolean(fichaAnchorEl)}
            anchorEl={fichaAnchorEl}
            onClose={handleCloseFichaPopover}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'center',
            }}
            transformOrigin={{
                vertical: 'top',
                horizontal: 'center',
            }}
        >
            <Box sx={{ p: 2, minWidth: 400 }}>
                <Typography variant="h6" gutterBottom>
                    Buscar Issue no JIRA
                </Typography>
                
                {fichaError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {fichaError}
                    </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Número da Ficha"
                        value={numeroFicha}
                        onChange={(e) => setNumeroFicha(e.target.value)}
                        disabled={searching}
                        error={Boolean(fichaError)}
                        helperText={fichaError}
                    />
                    <Button
                        variant="contained"
                        onClick={searchJiraIssues}
                        disabled={searching || !numeroFicha.trim()}
                        startIcon={searching ? <CircularProgress size={20} /> : <SearchIcon />}
                    >
                        {searching ? 'Buscando...' : 'Buscar'}
                    </Button>
                </Box>

                {jiraIssues.length > 0 && (
                    <>
                        <Typography variant="subtitle2" gutterBottom>
                            Selecione a Issue:
                        </Typography>
                        <RadioGroup
                            value={selectedJiraIssue ? selectedJiraIssue.id : ''}
                            onChange={(e, value) => setSelectedJiraIssue(jiraIssues.find(issue => issue.id === value))}
                        >
                            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                                {jiraIssues.map((issue) => (
                                    <ListItem key={issue.id} divider>
                                        <FormControlLabel
                                            value={issue.id}
                                            control={<Radio />}
                                            label={
                                                <Box>
                                                    <Typography variant="subtitle1">
                                                        {issue.key} - {issue.fields.summary}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary">
                                                        Status: {issue.fields.status.name}
                                                    </Typography>
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </RadioGroup>
                    </>
                )}

                {selectedJiraIssue && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={attachToPDF}
                                    onChange={(e) => setAttachToPDF(e.target.checked)}
                                />
                            }
                            label="Anexar PDF à issue do JIRA"
                            sx={{ mb: 2 }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => handleGeneratePDF(analysisId)}
                            disabled={!selectedJiraIssue}
                        >
                            Gerar PDF
                        </Button>
                    </>
                )}
            </Box>
        </Popover>
    );

    return (
        <Box sx={{ 
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
            py: 4,
            position: 'relative',
            overflow: 'hidden'
        }}>
            {loading ? (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center',
                    minHeight: 'calc(100vh - 200px)'
                }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Container maxWidth="lg">
                    <Fade in={true} timeout={1000}>
                        <Box sx={{ my: 4 }}>
                            {/* Card de Ficha em Análise - Estilo similar ao histórico */}
                            <Zoom in={true} timeout={500}>
                                <Paper
                                    elevation={0}
                                    sx={{ 
                                        p: 3, 
                                        mb: 4,
                                        borderRadius: 4,
                                        backgroundColor: 'background.paper',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid',
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'rgba(0, 0, 0, 0.1)',
                                        position: 'relative',
                                        overflow: 'hidden',
                                        textAlign: 'center',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            right: 0,
                                            height: '4px',
                                            background: 'linear-gradient(90deg, primary.main, primary.light)'
                                        }
                                    }}
                                >
                                    <Typography 
                                        variant="h6" 
                                        gutterBottom
                                        sx={{ 
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            mb: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <QuestionAnswerIcon color="primary" />
                                        Ficha em Análise
                                    </Typography>
                                    
                                    <Box sx={{ 
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        alignItems: 'center',
                                        gap: 1 
                                    }}>
                                        {numeroFicha ? (
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    fontWeight: 600,
                                                    color: theme.palette.primary.main
                                                }}
                                            >
                                                Nº {numeroFicha}
                                            </Typography>
                                        ) : (
                                            <Typography 
                                                variant="body1" 
                                                sx={{ 
                                                    fontStyle: 'italic',
                                                    color: theme.palette.text.secondary
                                                }}
                                            >
                                                Número não informado
                                            </Typography>
                                        )}
                                        
                                        {selectedAct && (
                                            <Chip
                                                label={selectedAct}
                                                color="primary"
                                                variant="outlined"
                                                size="small"
                                                sx={{ 
                                                    fontWeight: 500,
                                                    backgroundColor: theme.palette.mode === 'dark'
                                                        ? alpha(theme.palette.primary.main, 0.1)
                                                        : alpha(theme.palette.primary.main, 0.05)
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Paper>
                            </Zoom>

                            {/* Histórico de respostas */}
                            {history.length > 0 && (
                                <Zoom in={true} timeout={500}>
                                    <Paper 
                                        elevation={0}
                                        sx={{ 
                                            p: 3, 
                                            mb: 4,
                                            borderRadius: 4,
                                            backgroundColor: 'background.paper',
                                            backdropFilter: 'blur(20px)',
                                            border: '1px solid',
                                            borderColor: theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.1)'
                                                : 'rgba(0, 0, 0, 0.1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                right: 0,
                                                height: '4px',
                                                background: 'linear-gradient(90deg, primary.main, primary.light)'
                                            }
                                        }}
                                    >
                                        <Typography 
                                            variant="h6" 
                                            gutterBottom
                                            sx={{ 
                                                color: 'primary.main',
                                                fontWeight: 600,
                                                mb: 3,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <HistoryIcon color="primary" />
                                            <Box component="span" sx={{ 
                                                minWidth: 24, 
                                                height: 24, 
                                                borderRadius: '12px',
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontSize: '0.875rem',
                                                px: 1
                                            }}>
                                                {history.length}
                                            </Box>
                                            Histórico de Respostas
                                        </Typography>
                                        <Stack spacing={2}>
                                            {history.map((item, index) => (
                                                <Fade key={index} in={true} timeout={500 + index * 100}>
                                                    <Paper
                                                        elevation={0}
                                                        onClick={() => handleHistoryClick(index)}
                                                        sx={{ 
                                                            p: 2.5,
                                                            backgroundColor: theme.palette.mode === 'dark'
                                                                ? alpha(theme.palette.primary.main, 0.08)
                                                                : alpha(theme.palette.primary.main, 0.05),
                                                            borderRadius: 3,
                                                            border: '1px solid',
                                                            borderColor: theme.palette.mode === 'dark'
                                                                ? alpha(theme.palette.primary.main, 0.2)
                                                                : alpha(theme.palette.primary.main, 0.1),
                                                            transition: 'all 0.3s ease',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                backgroundColor: theme.palette.mode === 'dark'
                                                                    ? alpha(theme.palette.primary.main, 0.15)
                                                                    : alpha(theme.palette.primary.main, 0.1),
                                                                transform: 'translateX(4px)',
                                                                borderColor: theme.palette.mode === 'dark'
                                                                    ? alpha(theme.palette.primary.main, 0.3)
                                                                    : alpha(theme.palette.primary.main, 0.2),
                                                            }
                                                        }}
                                                    >
                                                        <Stack spacing={1}>
                                                            <Typography 
                                                                variant="body2" 
                                                                color="text.secondary"
                                                                sx={{ 
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1
                                                                }}
                                                            >
                                                                <QuestionAnswerIcon fontSize="small" color="primary" />
                                                                {processText(item.question)}
                                                            </Typography>
                                                            <Typography 
                                                                variant="body1" 
                                                                sx={{ 
                                                                    fontWeight: 500,
                                                                    pl: 3,
                                                                    color: 'text.primary'
                                                                }}
                                                            >
                                                                <NavigateNextIcon 
                                                                    sx={{ 
                                                                        verticalAlign: 'middle',
                                                                        mr: 1,
                                                                        color: 'primary.main'
                                                                    }} 
                                                                />
                                                                {processText(item.answer)}
                                                            </Typography>
                                                        </Stack>
                                                    </Paper>
                                                </Fade>
                                            ))}
                                        </Stack>
                                    </Paper>
                                </Zoom>
                            )}

                            {/* Pergunta atual ou Conclusão */}
                            <Zoom in={true} timeout={500}>
                                <Paper 
                                    elevation={0}
                                    sx={{ 
                                        borderRadius: 4,
                                        overflow: 'hidden',
                                        transition: 'all 0.3s',
                                        backgroundColor: 'background.paper',
                                        backdropFilter: 'blur(20px)',
                                        border: '1px solid',
                                        borderColor: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.1)'
                                            : 'rgba(0, 0, 0, 0.1)',
                                        position: 'relative'
                                    }}
                                >
                                    {renderQuestionContent()}
                                </Paper>
                            </Zoom>

                            {/* Botões de navegação */}
                            <Box sx={{ 
                                mt: 4, 
                                display: 'flex', 
                                justifyContent: 'center', 
                                gap: 3,
                                py: 2
                            }}>
                                <Button
                                    variant="outlined"
                                    onClick={handleBack}
                                    disabled={questionStack.length === 0}
                                    startIcon={<ArrowBackIcon />}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        minWidth: '160px',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        backgroundColor: 'transparent',
                                        color: theme.palette.mode === 'dark' 
                                            ? alpha(theme.palette.primary.light, 0.9)
                                            : theme.palette.primary.main,
                                        borderColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.primary.light, 0.3)
                                            : alpha(theme.palette.primary.main, 0.5),
                                        boxShadow: 'none',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.main, 0.1)
                                                : alpha(theme.palette.primary.main, 0.05),
                                            borderColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.primary.light, 0.5)
                                                : theme.palette.primary.main,
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 4px 8px rgba(0, 0, 0, 0.3)'
                                                : '0 4px 8px rgba(33, 150, 243, 0.15)'
                                        },
                                        '&:disabled': {
                                            backgroundColor: 'transparent',
                                            color: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.text.primary, 0.3)
                                                : alpha(theme.palette.text.primary, 0.4),
                                            borderColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.divider, 0.1)
                                                : alpha(theme.palette.divider, 0.2)
                                        }
                                    }}
                                >
                                    Voltar uma questão
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleReset}
                                    disabled={loading}
                                    startIcon={<RestartAltIcon />}
                                    sx={{
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: 2,
                                        minWidth: '160px',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 500,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.error.dark, 0.8)
                                            : theme.palette.error.main,
                                        color: '#fff',
                                        border: 'none',
                                        boxShadow: theme.palette.mode === 'dark'
                                            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                            : '0 2px 8px rgba(211, 47, 47, 0.3)',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? theme.palette.error.dark
                                                : alpha(theme.palette.error.dark, 0.9),
                                            transform: 'translateY(-2px)',
                                            boxShadow: theme.palette.mode === 'dark'
                                                ? '0 4px 12px rgba(0, 0, 0, 0.4)'
                                                : '0 4px 12px rgba(211, 47, 47, 0.4)'
                                        },
                                        '&:disabled': {
                                            backgroundColor: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.error.dark, 0.3)
                                                : alpha(theme.palette.error.main, 0.3),
                                            color: theme.palette.mode === 'dark'
                                                ? alpha(theme.palette.common.white, 0.4)
                                                : alpha(theme.palette.common.white, 0.6)
                                        }
                                    }}
                                >
                                    Reiniciar
                                </Button>
                            </Box>
                        </Box>
                    </Fade>
                </Container>
            )}
            
            {/* Elementos decorativos de fundo */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '5%',
                    left: '2%',
                    width: '300px',
                    height: '300px',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0) 70%)'
                        : 'radial-gradient(circle, rgba(25, 118, 210, 0.08) 0%, rgba(25, 118, 210, 0) 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '5%',
                    width: '250px',
                    height: '250px',
                    background: theme.palette.mode === 'dark'
                        ? 'radial-gradient(circle, rgba(33, 203, 243, 0.05) 0%, rgba(33, 203, 243, 0) 70%)'
                        : 'radial-gradient(circle, rgba(33, 203, 243, 0.08) 0%, rgba(33, 203, 243, 0) 70%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                }}
            />

            {renderJiraDialog()}
            {renderPDFPopover()}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbarOpen(false)} 
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

const QuestionNode = ({ data, selected }) => {
    const theme = useTheme();
    
    // Determina a cor de fundo com base no tipo de conclusão
    const getBackgroundColor = () => {
        if (!data.is_final) {
            return theme.palette.background.paper;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return alpha(theme.palette.success.main, 0.1);
        } else if (data.is_success === false) {
            return alpha(theme.palette.error.main, 0.1);
        } else {
            return alpha(theme.palette.info.main, 0.1);
        }
    };
    
    // Determina a cor da borda com base no tipo de conclusão
    const getBorderColor = () => {
        if (selected) {
            return theme.palette.primary.main;
        }
        
        if (!data.is_final) {
            return theme.palette.divider;
        }
        
        // Se for uma conclusão, verifica o tipo
        if (data.is_success === true) {
            return alpha(theme.palette.success.main, 0.3);
        } else if (data.is_success === false) {
            return alpha(theme.palette.error.main, 0.3);
        } else {
            return alpha(theme.palette.info.main, 0.3);
        }
    };
    
    // Determina a cor do texto do tipo de nó
    const getTypeColor = () => {
        if (!data.is_final) {
            return theme.palette.text.secondary;
        }
        
        if (data.is_success === true) {
            return theme.palette.success.main;
        } else if (data.is_success === false) {
            return theme.palette.error.main;
        } else {
            return theme.palette.info.main;
        }
    };
    
    // Determina o ícone do tipo de conclusão
    const getTypeIcon = () => {
        if (!data.is_final) {
            return null;
        }
        
        if (data.is_success === true) {
            return <CheckCircleIcon fontSize="small" sx={{ ml: 1 }} />;
        } else if (data.is_success === false) {
            return <CancelIcon fontSize="small" sx={{ ml: 1 }} />;
        }
        
        return null;
    };
    
    return (
        <Paper
            sx={{
                p: 3,
                minWidth: 300,
                maxWidth: 400,
                backgroundColor: getBackgroundColor(),
                border: '1px solid',
                borderColor: getBorderColor(),
                borderRadius: 2,
                position: 'relative',
                boxShadow: selected 
                    ? `0 0 0 2px ${theme.palette.primary.main}`
                    : theme.shadows[2],
                transition: 'all 0.2s ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[4]
                }
            }}
        >
            {/* Botão para definir como primeira questão */}
            {!data.is_final && (
                <Tooltip title={data.id === data.flow_first_question_id ? "Primeira Questão" : "Definir como Primeira Questão"}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            data.onSetAsFirstQuestion(data.id);
                        }}
                        sx={{
                            position: 'absolute',
                            top: -12,
                            right: -12,
                            width: 24,
                            height: 24,
                            backgroundColor: data.id === data.flow_first_question_id 
                                ? theme.palette.primary.main 
                                : alpha(theme.palette.background.paper, 0.9),
                            color: data.id === data.flow_first_question_id 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.text.secondary,
                            '&:hover': {
                                backgroundColor: data.id === data.flow_first_question_id 
                                    ? theme.palette.primary.dark 
                                    : alpha(theme.palette.primary.main, 0.1),
                                transform: 'scale(1.1)'
                            },
                            border: '2px solid',
                            borderColor: data.id === data.flow_first_question_id 
                                ? theme.palette.primary.main 
                                : theme.palette.divider,
                            boxShadow: data.id === data.flow_first_question_id 
                                ? theme.shadows[2] 
                                : 'none',
                            zIndex: 1000
                        }}
                    >
                        <PlayCircleOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}

            {/* Handle de entrada no topo */}
            <Handle
                type="target"
                position={Position.Top}
                style={{ 
                    background: theme.palette.primary.main,
                    width: 8,
                    height: 8,
                    top: -4,
                    zIndex: 1000,
                    border: `2px solid ${theme.palette.background.paper}`
                }}
            />

            {/* Cabeçalho com tipo e referência */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                mb: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography 
                        variant="caption" 
                        component="div"
                        sx={{ 
                            color: getTypeColor(),
                            fontWeight: 500,
                            px: 1,
                            py: 0.5,
                            backgroundColor: alpha(getTypeColor(), 0.1),
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {data.is_final ? 'Conclusão' : 'Pergunta'}
                        {getTypeIcon()}
                    </Typography>
                </Box>
                {data.article_reference && (
                    <Typography 
                        variant="caption" 
                        component="div"
                        sx={{ 
                            color: theme.palette.text.secondary,
                            fontSize: '0.75rem',
                            px: 1,
                            py: 0.5,
                            backgroundColor: alpha(theme.palette.background.default, 0.5),
                            borderRadius: 1,
                            maxWidth: '60%'
                        }}
                    >
                        Ref: {data.article_reference}
                    </Typography>
                )}
            </Box>

            {/* Texto principal */}
            <Typography 
                variant="body1" 
                sx={{ 
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    mb: 3
                }}
            >
                {data.text}
            </Typography>
            
            {/* Opções e edges */}
            {!data.is_final && data.options && (
                <Box sx={{ mt: 2 }}>
                    <Box 
                        sx={{ 
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            position: 'relative',
                            zIndex: 0
                        }}
                    >
                        {data.options.map((option, index) => (
                            <Box 
                                key={index} 
                                sx={{ 
                                    position: 'relative',
                                    pb: 3,
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center'
                                }}
                            >
                                <Paper
                                    sx={{
                                        p: 1.5,
                                        width: '100%',
                                        textAlign: 'center',
                                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                        border: '1px solid',
                                        borderColor: alpha(theme.palette.primary.main, 0.2),
                                        borderRadius: 1.5,
                                        position: 'relative',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.2),
                                            transform: 'translateY(-2px)'
                                        },
                                        zIndex: 0
                                    }}
                                >
                                    <Typography 
                                        variant="body2"
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontWeight: 500
                                        }}
                                    >
                                        {option}
                                    </Typography>
                                </Paper>
                                
                                {/* Handle de saída para cada opção */}
                                <Handle
                                    type="source"
                                    position={Position.Bottom}
                                    id={index.toString()}
                                    style={{
                                        background: theme.palette.primary.main,
                                        width: 8,
                                        height: 8,
                                        bottom: 0,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        zIndex: 1000,
                                        border: `2px solid ${theme.palette.background.paper}`
                                    }}
                                />
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Mensagem final para conclusões */}
            {data.is_final && data.final_message && (
                <Box sx={{ mt: 2 }}>
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                            fontStyle: 'italic',
                            backgroundColor: alpha(
                                data.is_success === true 
                                    ? theme.palette.success.main 
                                    : data.is_success === false 
                                        ? theme.palette.error.main 
                                        : theme.palette.info.main, 
                                0.1
                            ),
                            p: 2,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: alpha(
                                data.is_success === true 
                                    ? theme.palette.success.main 
                                    : data.is_success === false 
                                        ? theme.palette.error.main 
                                        : theme.palette.info.main, 
                                0.2
                            )
                        }}
                    >
                        <DynamicText text={data.final_message} />
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};

export default DecisionTreeWrapper; 